import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { BreezeChannelIdentifier } from '@microsoft/applicationinsights-common';
import { ClickAnalyticsPlugin } from '@microsoft/applicationinsights-clickanalytics-js';
import { CognitoConfiguration } from '@cognitoforms/utils/clientside-configuration';
import { isPrerender } from '@cognitoforms/utils/utilities';
import { serviceRequest } from '@cognitoforms/api/services/service-request';
import Vue from 'vue';
import type { IApplicationInsights } from '@microsoft/applicationinsights-web';

let appInsights: ApplicationInsights = null;
let clickAnalytics: ClickAnalyticsPlugin = null;

function relayFailedTelemetry(payload: string[]) {
	const items = payload.map(item => {
		try {
			const envelope = JSON.parse(item);
			envelope.iKey = 'faux-key';
			return envelope;
		}
		catch (e) {
			return null;
		}
	}).filter(Boolean);

	if (items.length)
		serviceRequest('svc/log', items, { method: 'POST' }).catch(() => { });
}

function hookSenderFailures(ai: ApplicationInsights) {
	const senderPlugin = ai.core.getPlugin(BreezeChannelIdentifier)?.plugin as any;
	if (!senderPlugin || typeof senderPlugin._onError !== 'function')
		return;

	const originalOnError = senderPlugin._onError.bind(senderPlugin);
	senderPlugin._onError = (payload: string[], message: string, event?: Event) => {
		originalOnError(payload, message, event);
		relayFailedTelemetry(payload);
	};
}

if (!isPrerender() && process.env.NODE_ENV !== 'development' && process.env.NODE_ENV !== 'test') {
	try {
		clickAnalytics = new ClickAnalyticsPlugin();
		appInsights = new ApplicationInsights({
			config: {
				connectionString: CognitoConfiguration.AppInsightsConnectionString,
				excludeRequestFromAutoTrackingPatterns: [/\/svc\/log(\?|$)/],
				extensions: [clickAnalytics],
				extensionConfig: {
					[clickAnalytics.identifier]: {
						autoCapture: true,
						dropInvalidEvents: true,
						dataTags: {
							customDataPrefix: 'data-cf-',
							useDefaultContentNameOrId: false
						}
					}
				}
			}
		});
		appInsights.loadAppInsights();
		hookSenderFailures(appInsights);
	}
	catch (e) {
		appInsights = null;
		clickAnalytics = null;
		console.warn('AI failed to initialize:', e);
	}
}

export const AppInsights: () => IApplicationInsights = () => appInsights;

export const ClickAnalytics: () => ClickAnalyticsPlugin = () => clickAnalytics;

export default Vue.extend({
	name: 'CAppInsights',
	data() {
		return {
			appInsights
		};
	},
	watch: {
		$route: {
			immediate: true,
			handler(route) {
				if (appInsights && !route.meta.customReadyEvent)
					appInsights.stopTrackPage(route.name ?? route.path);
			}
		}
	},
	render() {
		return this.$slots.default;
	}
});